/**
 * Pure access decision for a flag-gated ROUTE — a leaf module with no React / Supabase /
 * app-mode imports, so the semantics are unit-testable in isolation. Same shape and reasons
 * as `resolveGuardState` in src/lib/auth/guard-state.ts, which does this job for auth.
 *
 * WHY NOT `resolveFlag`
 * ---------------------
 * `resolveFlag` answers a RENDER question and returns a bare boolean, folding "still
 * loading" into the caller's default. For a card or a button that is correct and cheap: the
 * wrong answer costs one frame and fixes itself.
 *
 * A redirect is not like that. It is not idempotent and it is not recoverable from inside
 * the screen, because the screen is gone. Deciding it on a default would mean:
 *
 *   - every cold navigation redirects, since the flag set has not loaded on first render —
 *     including for a user who genuinely holds the flag;
 *   - mock/demo mode redirects forever, because `enabled_features` is never called there.
 *
 * So LOADING stays a third answer, and the caller must render something neutral until the
 * answer is real.
 */
export type FlagRouteAccess = 'loading' | 'allowed' | 'redirect';

/**
 * Precedence is deliberate, mirroring `resolveGuardState`:
 *
 *   1. mock     — sample data never calls the flag RPC, so a remote flag cannot be the
 *                 thing that decides a demo. Open, or the demo is dead.
 *   2. loading  — never decide on incomplete information. Covers both "fetching" and the
 *                 idle/disabled query that reports `isLoading: false` with no result yet.
 *   3. error    — fail CLOSED. Unlike the auth guard there is no retry screen: this gates a
 *                 product seeded OFF globally, the only way to arrive on a failed fetch is a
 *                 deep link (every in-app launcher is flag-gated and already hidden), and
 *                 landing on the parent screen loses the user nothing.
 *   4. resolved — the flag set is known; the key is in it or it is not.
 */
export function resolveFlagRouteAccess(args: {
  key: string;
  isMock: boolean;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  enabledKeys: string[] | undefined;
}): FlagRouteAccess {
  const { key, isMock, isLoading, isError, isSuccess, enabledKeys } = args;

  if (isMock) return 'allowed';
  if (isLoading) return 'loading';
  if (isError) return 'redirect';
  // Not yet resolved and not an error: the query is idle or disabled. Still unknown.
  if (!isSuccess || !enabledKeys) return 'loading';

  return enabledKeys.includes(key) ? 'allowed' : 'redirect';
}
